// Background audio of the open task step: one file at a time (the native apps play only the first
// audio of a step). Lives outside the components so that a remount of the spot details (refreshView
// after a teammate solve) resumes the file instead of restarting it.
//
// State is only read through the action context: vuex-persist replaces the root state on startup,
// so a module-scope state object would be detached from the store.
const state = () => ({
    src: '',
    // optimistic: set when playback is requested, corrected by the element events and a rejected play()
    playing: false
});

// one <audio> element for the whole session, never attached to the DOM; not reactive state
let audio = null;
let loadedSrc = '';
let lastStep = null;
let pausedBySystem = false;
let pausedByUser = false;
// a play() rejected after the source changed must not flip the state of the new source
let playToken = 0;

const UNLOCK_EVENTS = ['touchend', 'click', 'keydown'];

const getters = {
    playing(state) {
        return state.playing;
    }
};

function getAudio(commit) {
    if (audio === null) {
        audio = new Audio();
        audio.preload = 'auto';
        // external pause/resume (OS media controls, unplugged headphones); the guards skip the stale
        // events a source change queues behind an already started play()
        audio.addEventListener('play', () => {
            if (!audio.paused) {
                commit('SET_PLAYING', true);
            }
        });
        audio.addEventListener('pause', () => {
            if (audio.paused) {
                commit('SET_PLAYING', false);
            }
        });
        audio.addEventListener('ended', () => commit('SET_PLAYING', false));
        audio.addEventListener('error', () => commit('SET_PLAYING', false));
    }
    return audio;
}

function play({ commit }) {
    const element = getAudio(commit);
    const token = ++playToken;
    commit('SET_PLAYING', true);
    const request = element.play();
    if (request && typeof request.catch === 'function') {
        // blocked autoplay (no user gesture yet) stays silent; the button is the gesture then
        request.catch(() => {
            if (token === playToken) {
                commit('SET_PLAYING', false);
            }
        });
    }
}

function pause({ commit }) {
    if (audio) {
        audio.pause();
    }
    commit('SET_PLAYING', false);
}

function loadSource(context, src, autoplay) {
    const element = getAudio(context.commit);
    element.src = src;
    element.load();
    loadedSrc = src;
    lastStep = null;
    pausedBySystem = false;
    pausedByUser = false;
    if (autoplay) {
        play(context);
    } else {
        context.commit('SET_PLAYING', false);
    }
}

function unload({ commit }) {
    if (audio && loadedSrc) {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
    }
    loadedSrc = '';
    lastStep = null;
    pausedBySystem = false;
    pausedByUser = false;
    commit('SET_PLAYING', false);
}

function suspend(context) {
    if (context.state.playing) {
        pausedBySystem = true;
        pause(context);
    }
}

const actions = {
    // iOS Safari only lets an <audio> element play programmatically after it was created or load()ed
    // inside a user gesture (the same trick Howler uses for its node pool). Catches the first gesture
    // in the app, e.g. the login tap, so the first spot can already autoplay.
    primeUnlock(context) {
        const unlock = () => {
            UNLOCK_EVENTS.forEach((event) => document.removeEventListener(event, unlock, true));
            // a running file is unlocked already; load() would restart it
            if (!context.state.playing) {
                getAudio(context.commit).load();
            }
        };
        UNLOCK_EVENTS.forEach((event) => document.addEventListener(event, unlock, true));
    },

    // Single entry point of the spot details: the visible step's audio and step, whether the details
    // are visible (active) and whether the step may autoplay. Diffs against the previous call.
    sync(context, { src, step, active, autoplay }) {
        const { commit, state } = context;
        if (!src) {
            unload(context);
            commit('SET_SRC', '');
            return;
        }
        const sameSrc = src === loadedSrc;
        commit('SET_SRC', src);
        if (!active) {
            // hidden details only pause; a different file is dropped and loaded once visible again
            if (sameSrc) {
                suspend(context);
            } else {
                unload(context);
            }
            return;
        }
        if (!sameSrc) {
            loadSource(context, src, autoplay);
        } else if (pausedBySystem) {
            pausedBySystem = false;
            play(context);
        } else if (step !== lastStep && autoplay && !state.playing && !pausedByUser) {
            // same file in the next step: a finished or paused one starts again, a running one goes on
            play(context);
        }
        lastStep = step;
    },

    // Pause while the details are hidden or destroyed temporarily; sync resumes it. A file the user
    // paused or that has ended is left alone so it does not come back on its own.
    suspend(context) {
        suspend(context);
    },

    toggle(context) {
        const { state } = context;
        if (state.playing) {
            pausedByUser = true;
            pausedBySystem = false;
            pause(context);
            return;
        }
        pausedByUser = false;
        pausedBySystem = false;
        if (loadedSrc) {
            play(context);
        } else if (state.src) {
            loadSource(context, state.src, true);
        }
    },

    stop(context) {
        unload(context);
        context.commit('SET_SRC', '');
    }
};

const mutations = {
    SET_SRC(state, src) {
        state.src = src;
    },
    SET_PLAYING(state, playing) {
        state.playing = playing;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
